import { action, computed, makeObservable, observable } from "mobx";
import { ErrorMessages } from "../../helpers/errorMessage";
import { ValidatedValue } from "../../helpers/validator";
import { CalculateStore } from "../../stores/calculateStore";
import { DefaultRequestStatus, RequestStatus } from "./requestStatus";
import { WidgetApiService } from "./widgetApiService";
import Validator, { ValidatorModel } from "../../helpers/validator";
import { ProposalRequestMapper } from "./proposalRequestMapper";
import { CalculationRequestMapper, DefaultCalculationRequestMapperBase } from "./calculationRequestMapper";
import { AdditionalQuestionsModel } from "../../helpers/additionalQuestions/additionalQuestions";

export interface StoreCalculationResponse {
  calculationId?: string;
  ratesStored?: boolean;
  ratesFailureReason?: RatesFailureReason;
}

export interface RatesFailureReason {
  partnerApiErrorCodes?: string[];
  oldApiMessage?: string;
}

export const saveValidators: ValidatorModel = {
  calculationName: [
    { name: "calculationNameIsNotEmpty", fn: Validator.isNotEmpty, errorMessage: ErrorMessages.required }
  ]
};

export interface StoreCalculationService {
  requestStatus: RequestStatus;
  storeCalculationResponse: StoreCalculationResponse;
  saveName: ValidatedValue<string>;

  setSaveName: (name: string) => void;
  saveCalculationAndProposal: () => any;
  setStoreCalculationResponse: (response: StoreCalculationResponse) => void;
  reset: () => void;
}

export class DefaultStoreCalculationService implements StoreCalculationService {
  constructor(private widgetApiService: WidgetApiService,
    private calculationRequestMapperForStore: CalculationRequestMapper, private calculateStore: CalculateStore,
    private proposalRequestMapper: ProposalRequestMapper) {
    makeObservable(this);
  }

  @observable
  private _requestStatus: RequestStatus = new DefaultRequestStatus();

  @computed
  public get requestStatus(): RequestStatus {
    return this._requestStatus;
  }

  @observable
  private _saveName: ValidatedValue<string> = new ValidatedValue("", saveValidators.calculationName);

  @computed
  public get saveName(): any {
    return this._saveName;
  }

  @action
  public setSaveName(name: string) {
    this._saveName.value = name;
  }

  @observable
  private _storeCalculationResponse: any = { calculationId: undefined };

  @computed
  public get storeCalculationResponse(): any {
    return this._storeCalculationResponse;
  }

  @action
  public setStoreCalculationResponse(response: StoreCalculationResponse) {
    this._storeCalculationResponse = response;
  }

  @action
  public reset() {
    this.requestStatus.reset();
    this.setStoreCalculationResponse({});
    this.saveName.clearValidation();
  }

  @action
  public saveCalculationAndProposal() {
    this.saveRequest(
      this.prepareRequest()
    ).then((resp) => {
      if (resp) {
        this.setStoreCalculationResponse(resp);
        this.calculateStore.setParentCalculationId(resp.calculationId);
      }
    }).catch((error) => {
      this.handleError(error);
    });
  }

  private handleError(error: any) {
    // tslint:disable-next-line:no-console
    console.log("Błąd: ", error);
  }

  private saveRequest(data: any): Promise<any> {
    return this.requestStatus.handleRequest(
      () => this.widgetApiService.storeCalculation(data)
    );
  }

  private prepareRequest() {
    const selectedPackageId = this.calculateStore!.selectedPackage;
    const packageData = this.calculateStore.packages.get(selectedPackageId)!;

    return {
      calculationRequest: this.calculationRequestMapperForStore.prepareRequestForSummaryBox(packageData),
      proposalRequest: this.proposalRequestMapper.prepareRequest(undefined),
      calculationName: this.saveName.value
    };
  }
}

export class CalculationRequestMapperForStore extends DefaultCalculationRequestMapperBase {
  public prepareCourseOfInsurance() {
    const additionalQuestions = this.calculateStore.additionalQuestions;

    return {
      claimlessYearsIn5Years: additionalQuestions.claimlessYearsIn5Years.value,
      numberOfDamagesInLastThreeYears: additionalQuestions.numberOfDamagesInLastThreeYears.value
    };
  }

  public preparePaymentMode() {
    return this.calculateStore.additionalQuestions.paymentMode.value || undefined;
  }

  public prepareInsuranceDuration() {
    return this.calculateStore.additionalQuestions.policyDuration.value;
  }

  protected mapPartOfFarm(estate: any, additionalQuestions: AdditionalQuestionsModel) {
    Object.assign(estate, {
      partOfFarm: additionalQuestions.partOfFarm.value
    });
  }

  protected mapBusinessActivity(estate: any, additionalQuestions: AdditionalQuestionsModel) {
    Object.assign(estate, {
      businessActivity: additionalQuestions.businessActivityInBuilding.value
    });
  }
}
